import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Building2, CreditCard, Zap, Shield, Users, Palette, X, Plus, Trash2, Check } from 'lucide-react';

const TIERS = [
  { key: 'recovery_core', name: 'Recovery Core', price: 1297 },
  { key: 'growth_accelerator', name: 'Growth Accelerator', price: 1997 },
  { key: 'premium_multi_location', name: 'Premium Multi-Location', price: 2997 },
];

export default function Settings() {
  const { api, user } = useAuth();
  const [clinic, setClinic] = useState(null);
  const [usage, setUsage] = useState(null);
  const [automations, setAutomations] = useState([]);
  const [team, setTeam] = useState([]);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', password: '', role: 'FRONT_DESK' });
  const [clinicForm, setClinicForm] = useState({ clinic_name: '', owner_name: '', avg_case_value: '' });
  const [planMessage, setPlanMessage] = useState('');

  useEffect(() => {
    // Detect ?billing=cancelled from Stripe Checkout cancellation
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') === 'cancelled') {
      setPlanMessage('Checkout was cancelled — no charges were made.');
      window.history.replaceState({}, '', '/settings');
      setTimeout(() => setPlanMessage(''), 5000);
    }
    // Auto-trigger checkout if coming straight from a pricing CTA on the landing page
    const autoTier = params.get('auto_checkout');
    if (autoTier && ['recovery_core', 'growth_accelerator', 'premium_multi_location'].includes(autoTier)) {
      window.history.replaceState({}, '', '/settings');
      (async () => {
        try {
          setPlanMessage('Redirecting to secure Stripe Checkout...');
          const { data } = await api().post('/api/billing/checkout', {
            tier: autoTier,
            origin_url: window.location.origin,
          });
          if (data?.url) window.location.href = data.url;
        } catch (err) {
          setPlanMessage(err.response?.data?.detail || 'Failed to start checkout');
          setTimeout(() => setPlanMessage(''), 4000);
        }
      })();
    }

    async function load() {
      try {
        const [cRes, uRes, aRes, tRes, bRes] = await Promise.all([
          api().get('/api/clinics/me'),
          api().get('/api/billing/usage'),
          api().get('/api/settings/automations'),
          api().get('/api/team'),
          api().get('/api/settings/branding'),
        ]);
        setClinic(cRes.data);
        setUsage(uRes.data);
        setAutomations(aRes.data);
        setTeam(tRes.data);
        setBranding(bRes.data);
        setClinicForm({
          clinic_name: cRes.data.clinic_name || '',
          owner_name: cRes.data.owner_name || '',
          avg_case_value: cRes.data.avg_case_value || '',
        });
      } catch (e) { console.error(e); }
      setLoading(false);
    }
    load();
  }, [api]);

  const saveClinic = async () => {
    setSaving(true);
    try {
      const updates = { ...clinicForm };
      if (updates.avg_case_value) updates.avg_case_value = parseFloat(updates.avg_case_value);
      const { data } = await api().put('/api/clinics/me', updates);
      setClinic(data);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const toggleAutomation = async (id) => {
    try {
      const { data } = await api().patch(`/api/settings/automations/${id}`);
      setAutomations(prev => prev.map(a => a.id === id ? { ...a, active: data.active } : a));
    } catch (e) { console.error(e); }
  };

  const inviteMember = async (e) => {
    e.preventDefault();
    try {
      await api().post('/api/team/invite', inviteForm);
      setShowInvite(false);
      setInviteForm({ name: '', email: '', password: '', role: 'FRONT_DESK' });
      const { data } = await api().get('/api/team');
      setTeam(data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to invite');
    }
  };

  const removeMember = async (id) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      await api().delete(`/api/team/${id}`);
      setTeam(prev => prev.filter(m => m.id !== id));
    } catch (e) { console.error(e); }
  };

  const changePlan = async (tier) => {
    try {
      setPlanMessage('Redirecting to secure Stripe Checkout...');
      const { data } = await api().post('/api/billing/checkout', {
        tier,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setPlanMessage('Failed to create checkout session');
      }
    } catch (err) {
      setPlanMessage(err.response?.data?.detail || 'Failed to start checkout');
      setTimeout(() => setPlanMessage(''), 4000);
    }
  };

  const openBillingPortal = async () => {
    try {
      const { data } = await api().post('/api/billing/portal', {
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      setPlanMessage(err.response?.data?.detail || 'Unable to open billing portal');
      setTimeout(() => setPlanMessage(''), 4000);
    }
  };

  const saveBranding = async () => {
    setSaving(true);
    try {
      const { data } = await api().put('/api/settings/branding', branding);
      setBranding(data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="settings-page" className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your clinic, team, and subscription</p>
      </div>

      {/* Clinic Details */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-4">
          <Building2 size={16} /> Clinic Details
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Clinic Name</label>
            <input data-testid="settings-clinic-name" value={clinicForm.clinic_name}
              onChange={e => setClinicForm(p => ({ ...p, clinic_name: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Owner Name</label>
            <input data-testid="settings-owner-name" value={clinicForm.owner_name}
              onChange={e => setClinicForm(p => ({ ...p, owner_name: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Average Case Value ($)</label>
            <input data-testid="settings-avg-value" type="number" value={clinicForm.avg_case_value}
              onChange={e => setClinicForm(p => ({ ...p, avg_case_value: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
              placeholder="5000" />
          </div>
          <button data-testid="settings-save" onClick={saveClinic} disabled={saving}
            className="bg-slate-900 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 transition-all disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Team Management */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Users size={16} /> Team Members
          </h3>
          <button data-testid="invite-member-btn" onClick={() => setShowInvite(true)}
            className="flex items-center gap-1.5 text-xs font-medium bg-slate-900 text-white px-3 py-1.5 rounded-lg hover:bg-slate-800 transition-all">
            <Plus size={12} /> Invite
          </button>
        </div>
        <div className="space-y-2">
          {team.map(m => (
            <div key={m.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-semibold text-slate-600">
                {m.name?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{m.name}</p>
                <p className="text-xs text-slate-500">{m.email}</p>
              </div>
              <span className="text-[10px] font-semibold bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full shrink-0">
                {m.role?.replace(/_/g, ' ')}
              </span>
              {m.id !== user?.id && (
                <button onClick={() => removeMember(m.id)} className="text-slate-400 hover:text-red-500 transition-colors shrink-0">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Billing */}
      {usage && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-4">
            <CreditCard size={16} /> Billing & Plan
          </h3>
          {planMessage && (
            <div className="bg-emerald-50 text-emerald-700 text-sm px-4 py-2 rounded-lg mb-4 flex items-center gap-2">
              <Check size={14} /> {planMessage}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500">Current Plan</p>
              <p className="text-lg font-bold text-slate-900 mt-1">{usage.plan}</p>
              <p className="text-sm text-slate-600">${usage.monthly_price}/month</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500">SMS Usage</p>
              <p className="text-lg font-bold text-slate-900 mt-1">{usage.sms_used} / {usage.sms_limit}</p>
              {usage.sms_overage > 0 && (
                <p className="text-xs text-amber-600 mt-1">Overage: {usage.sms_overage} (+${usage.overage_charge.toFixed(2)})</p>
              )}
            </div>
          </div>

          {/* Plan Selection */}
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              {clinic?.stripe_customer_id ? 'Change / Upgrade Plan' : 'Subscribe to a Plan'}
            </h4>
            {clinic?.stripe_customer_id && (
              <button
                data-testid="manage-billing-btn"
                onClick={openBillingPortal}
                className="text-xs font-medium text-slate-700 hover:text-slate-900 underline underline-offset-4"
              >
                Manage billing (cancel / update card)
              </button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {TIERS.map(t => (
              <button
                key={t.key}
                data-testid={`plan-${t.key}`}
                onClick={() => changePlan(t.key)}
                disabled={usage.tier === t.key && clinic?.subscription_status === 'active'}
                className={`p-4 rounded-lg border text-left transition-all ${
                  usage.tier === t.key && clinic?.subscription_status === 'active'
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white border-slate-200 hover:border-slate-400'
                } disabled:cursor-default`}
              >
                <p className={`text-sm font-semibold ${usage.tier === t.key && clinic?.subscription_status === 'active' ? 'text-white' : 'text-slate-900'}`}>{t.name}</p>
                <p className={`text-lg font-bold mt-1 ${usage.tier === t.key && clinic?.subscription_status === 'active' ? 'text-white' : 'text-slate-900'}`}>${t.price.toLocaleString()}/mo</p>
                {usage.tier === t.key && clinic?.subscription_status === 'active' && (
                  <p className="text-[10px] text-emerald-300 mt-1">Active subscription</p>
                )}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">
            Secure checkout powered by Stripe. You will be charged monthly until you cancel.
          </p>
        </div>
      )}

      {/* Automations */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-4">
          <Zap size={16} /> Automation Sequences
        </h3>
        <div className="space-y-3">
          {automations.map(seq => (
            <div key={seq.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-slate-900">{seq.sequence_name}</p>
                <p className="text-xs text-slate-500 mt-0.5">Type: {seq.sequence_type}</p>
              </div>
              <button
                data-testid={`toggle-automation-${seq.id}`}
                onClick={() => toggleAutomation(seq.id)}
                className={`relative w-11 h-6 rounded-full transition-colors ${seq.active ? 'bg-emerald-500' : 'bg-slate-300'}`}
              >
                <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform"
                  style={{ transform: seq.active ? 'translateX(20px)' : 'translateX(0)' }} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* White-Label / Branding */}
      {branding && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-4">
            <Palette size={16} /> Branding
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Logo URL</label>
              <input data-testid="branding-logo" value={branding.logo_url || ''}
                onChange={e => setBranding(p => ({ ...p, logo_url: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                placeholder="https://yoursite.com/logo.png" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Primary Color</label>
              <div className="flex items-center gap-3">
                <input type="color" value={branding.primary_color || '#0F172A'}
                  onChange={e => setBranding(p => ({ ...p, primary_color: e.target.value }))}
                  className="w-10 h-10 rounded border border-slate-200 cursor-pointer" />
                <input value={branding.primary_color || '#0F172A'}
                  onChange={e => setBranding(p => ({ ...p, primary_color: e.target.value }))}
                  className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Custom Domain <span className="text-slate-400">(Premium only)</span></label>
              <input data-testid="branding-domain" value={branding.custom_domain || ''}
                onChange={e => setBranding(p => ({ ...p, custom_domain: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                placeholder="app.yourclinic.com" />
            </div>
            <button onClick={saveBranding} disabled={saving}
              className="bg-slate-900 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 transition-all disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Branding'}
            </button>
          </div>
        </div>
      )}

      {/* HIPAA Notice */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-6">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-2">
          <Shield size={16} /> HIPAA Compliance
        </h3>
        <p className="text-xs text-slate-500 leading-relaxed">
          All patient data is encrypted at rest and in transit. Access is controlled via role-based permissions.
          BAA agreements are required with all third-party vendors before processing PHI in production.
        </p>
      </div>

      {/* Invite Modal */}
      {showInvite && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowInvite(false)}>
          <div className="bg-white rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-slate-900">Invite Team Member</h2>
              <button onClick={() => setShowInvite(false)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <form onSubmit={inviteMember} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Name *</label>
                <input data-testid="invite-name" required value={inviteForm.name}
                  onChange={e => setInviteForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Email *</label>
                <input data-testid="invite-email" type="email" required value={inviteForm.email}
                  onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Password *</label>
                <input data-testid="invite-password" type="password" required value={inviteForm.password}
                  onChange={e => setInviteForm(p => ({ ...p, password: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Role</label>
                <select data-testid="invite-role" value={inviteForm.role}
                  onChange={e => setInviteForm(p => ({ ...p, role: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white">
                  <option value="FRONT_DESK">Front Desk</option>
                  <option value="TREATMENT_COORDINATOR">Treatment Coordinator</option>
                  <option value="CLINIC_OWNER">Clinic Owner</option>
                </select>
              </div>
              <button data-testid="invite-submit" type="submit"
                className="w-full bg-slate-900 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-slate-800 transition-all">
                Send Invite
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
